import Http from "../Http";
import { LARAVEL_BACKEND_API_URL } from "../Helper";

// Get all reports (fetch from all projects)
export function getAllReports() {
  return (dispatch) =>
    new Promise(async (resolve, reject) => {
      try {
        const projectsResponse = await Http.get(`${LARAVEL_BACKEND_API_URL}projects`);
        const projects = Array.isArray(projectsResponse.data) 
          ? projectsResponse.data 
          : (projectsResponse.data.data || []);
        
        const allReportsPromises = projects.map(project => 
          Http.get(`${LARAVEL_BACKEND_API_URL}projects/${project.id}/reports`)
            .then(res => {
              const reportData = res.data;
              return Array.isArray(reportData) ? reportData : (reportData.data || []);
            })
            .catch(() => [])
        );
        
        const reportsArrays = await Promise.all(allReportsPromises);
        const allReports = reportsArrays.flat();
        
        resolve({ data: allReports });
      } catch (err) {
        reject(err);
      }
    });
}

// Get all reports from all projects (new endpoint)
export function getAllReportsFromAllProjects(page = 1) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.get(`${LARAVEL_BACKEND_API_URL}reports?page=${page}`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Get all reports for a project
export function getProjectReports(projectId, page = 1) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.get(`${LARAVEL_BACKEND_API_URL}projects/${projectId}/reports?page=${page}`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Create new report
export function createReport(projectId, data) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      // Add default cover_settings if not provided
      const reportData = {
        ...data,
        cover_settings: data.cover_settings || {
          show_cover_photo: true,
          show_company_logo: true,
          show_company_name: true,
          show_created_date: true,
          show_photo_count: true,
          show_claim_number: true,
        }
      };
      
      Http.post(`${LARAVEL_BACKEND_API_URL}projects/${projectId}/reports`, reportData)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Get single report
export function getReport(reportId) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.get(`${LARAVEL_BACKEND_API_URL}reports/${reportId}`)
        .then((res) => {
          const report = res.data;
          
          // Set default cover_settings if null
          if (!report.cover_settings) {
            report.cover_settings = {
              show_cover_photo: true,
              show_company_logo: true,
              show_company_name: true,
              show_created_date: true,
              show_photo_count: true,
              show_claim_number: true,
              cover_photo_id: null
            };
          }
          
          resolve(report);
        })
        .catch((err) => reject(err));
    });
}

// Update report
export function updateReport(reportId, data) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.put(`${LARAVEL_BACKEND_API_URL}reports/${reportId}`, data)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Delete report
export function deleteReport(reportId) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.delete(`${LARAVEL_BACKEND_API_URL}reports/${reportId}`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Get report sections
export function getReportSections(reportId) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.get(`${LARAVEL_BACKEND_API_URL}reports/${reportId}/sections`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Create section
export function createSection(reportId, data) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.post(`${LARAVEL_BACKEND_API_URL}reports/${reportId}/sections`, data)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Update section
export function updateSection(reportId, sectionId, data) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.put(`${LARAVEL_BACKEND_API_URL}reports/${reportId}/sections/${sectionId}`, data)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Delete section
export function deleteSection(reportId, sectionId) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.delete(`${LARAVEL_BACKEND_API_URL}reports/${reportId}/sections/${sectionId}`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Reorder sections
export function reorderSections(reportId, sectionIds) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.put(`${LARAVEL_BACKEND_API_URL}reports/${reportId}/sections/reorder`, { section_ids: sectionIds })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Add photo to section
export function addPhotoToSection(sectionId, data) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.post(`${LARAVEL_BACKEND_API_URL}sections/${sectionId}/photos`, data)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Add photos to section in batch
export function addPhotosBatchToSection(sectionId, data) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.post(`${LARAVEL_BACKEND_API_URL}sections/${sectionId}/photos/batch`, data)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Update photo
export function updateSectionPhoto(sectionId, photoId, data) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.put(`${LARAVEL_BACKEND_API_URL}sections/${sectionId}/photos/${photoId}`, data)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Remove photo from section
export function removePhotoFromSection(sectionId, photoId) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.delete(`${LARAVEL_BACKEND_API_URL}sections/${sectionId}/photos/${photoId}`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Reorder photos
export function reorderPhotos(sectionId, photoIds) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.put(`${LARAVEL_BACKEND_API_URL}sections/${sectionId}/photos/reorder`, { photo_ids: photoIds })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Generate PDF
export function generatePDF(reportId, companyData) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.post(`${LARAVEL_BACKEND_API_URL}reports/${reportId}/generate-pdf`, companyData)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Download PDF
export function downloadPDF(reportId, pdfId) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.get(`${LARAVEL_BACKEND_API_URL}reports/${reportId}/pdf/${pdfId}`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Update report settings
export function updateReportSettings(reportId, data) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.put(`${LARAVEL_BACKEND_API_URL}reports/${reportId}/settings`, data)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Get layout options
export function getLayoutOptions(reportId) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.get(`${LARAVEL_BACKEND_API_URL}reports/${reportId}/layout-options`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Update report sharing settings (authenticated)
export function updateReportShareSettings(reportId, data) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.put(`${LARAVEL_BACKEND_API_URL}reports/${reportId}/update-share-settings`, data)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// --- Public Sharing Services (No Auth needed) ---

// Get public report by token
export function getPublicReport(token) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.get(`${LARAVEL_BACKEND_API_URL}share/report/${token}`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Generate PDF for public report
export function generatePublicPDF(token) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.post(`${LARAVEL_BACKEND_API_URL}share/report/${token}/generate-pdf`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}

// Download PDF for public report
export function downloadPublicPDF(token, pdfId) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      Http.get(`${LARAVEL_BACKEND_API_URL}share/report/${token}/pdf/${pdfId}`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
}
